import React, { useState, useCallback, useRef } from 'react';
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  X,
  Users,
  Eye,
  EyeOff,
  Info,
  Loader2,
  FileX,

  Mail,
  Plus,
  ChevronLeft,
  ChevronRight,
  Hash
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { downloadRosterTemplate, parseRosterFileWithDuplicates, convertToDisplayDateFormat } from '../../utils/excelTemplate';

const BulkImportModal = ({ 
  isOpen, 
  onClose, 
  onImport,
  isSubmitting = false,
  existingRoster = [],
  importProgress = { current: 0, total: 0, message: '' }
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const fileInputRef = useRef(null);
  
  // States
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Duplicate Resolution, 4: Gender Assignment, 5: Import Complete
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [membersWithGender, setMembersWithGender] = useState([]);
  const [duplicateChoices, setDuplicateChoices] = useState({});
  const [memberDetails, setMemberDetails] = useState({}); // Store emails by member index
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInvalidRows, setShowInvalidRows] = useState(false);

  // Pagination state for preview tables
  const [previewCurrentPage, setPreviewCurrentPage] = useState(1);
  const [previewItemsPerPage, setPreviewItemsPerPage] = useState(10);
  const [duplicateCurrentPage, setDuplicateCurrentPage] = useState(1);
  const [duplicateItemsPerPage, setDuplicateItemsPerPage] = useState(10);
  const [genderCurrentPage, setGenderCurrentPage] = useState(1);
  const [genderItemsPerPage, setGenderItemsPerPage] = useState(10);

  // Reset modal state when opening/closing
  React.useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setStep(1);
    setUploadedFile(null);
    setParsedData(null);
    setPreviewData(null);
    setMembersWithGender([]);
    setDuplicateChoices({});
    setMemberDetails({});
    setError('');
    setIsProcessing(false);
    setShowInvalidRows(false);
    // Reset pagination states
    setPreviewCurrentPage(1);
    setDuplicateCurrentPage(1);
    setGenderCurrentPage(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Pagination helper function
  const createPaginationComponent = useCallback((currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, totalItems, items) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPageItems = items.slice(startIndex, endIndex);

    const PaginationControls = () => (
      <>
        {totalPages > 1 && (
          <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 p-3 rounded-lg border ${
            isDarkMode 
              ? "bg-gray-800 border-gray-600" 
              : "bg-gray-50 border-gray-200"
          }`}>
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                Show:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className={`select select-sm select-bordered ${
                  isDarkMode 
                    ? "bg-[#1a1f2d] border-white/10 text-white" 
                    : ""
                }`}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                per page
              </span>
            </div>

            {/* Page info */}
            <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} items
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`btn btn-sm btn-ghost ${
                  currentPage === 1 ? "btn-disabled" : ""
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`btn btn-sm ${
                        currentPage === pageNumber
                          ? "btn-primary"
                          : `btn-ghost ${isDarkMode ? "hover:bg-white/10" : ""}`
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`btn btn-sm btn-ghost ${
                  currentPage === totalPages ? "btn-disabled" : ""
                }`}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </>
    );

    return { currentPageItems, PaginationControls, startIndex };
  }, [isDarkMode]);

  // Clear errors when navigating between steps
  const clearErrors = () => {
    setError('');
  };

  // Handle template download
  const handleDownloadTemplate = useCallback(() => {
    try {
      downloadRosterTemplate();
    } catch (error) {
      setError('Failed to download template. Please try again.');
    }
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV or Excel file (.csv, .xls, .xlsx)');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('File is too large. Please keep it under 10MB.');
      return;
    }

    setUploadedFile(file);
    setError('');
    
    // Parse the file
    await processFile(file);
  }, []);

  // Process and parse the uploaded file
  const processFile = async (file) => {
    setIsProcessing(true);
    try {
      const result = await parseRosterFileWithDuplicates(file, existingRoster);
      setParsedData(result);
      setPreviewData(result.preview);
      setStep(2);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle proceeding to duplicate resolution
  const handleProceedToDuplicateResolution = useCallback(() => {
    clearErrors(); // Clear any existing errors
    const hasDuplicates = previewData?.summary?.hasDuplicates;
    
    if (hasDuplicates) {
      // Initialize duplicate choices - default to skipping duplicates
      const choices = {};
      
      // For internal duplicates (keep first occurrence, skip others)
      previewData.duplicates.internal.forEach(duplicate => {
        duplicate.indices.slice(1).forEach(index => {
          choices[`internal_${index}`] = false; // Skip by default
        });
      });
      
      // For roster duplicates (skip by default)
      previewData.duplicates.roster.forEach(duplicate => {
        choices[`roster_${duplicate.importIndex}`] = false; // Skip by default
      });
      
      setDuplicateChoices(choices);
      setStep(3); // Go to duplicate resolution step
    } else {
      // No duplicates, go directly to gender assignment
      handleProceedToGenderAssignment();
    }
  }, [previewData]);

  // Get filtered members based on duplicate choices
  const getFilteredMembers = useCallback(() => {
    const validMembers = previewData?.uniqueMembers || previewData?.validMembers || [];
    let membersToImport = [...validMembers];
    
    // Apply duplicate choices if we have them
    if (Object.keys(duplicateChoices).length > 0) {
      // Filter out internal duplicates that user chose to skip
      const internalSkipIndices = new Set();
      Object.entries(duplicateChoices).forEach(([key, include]) => {
        if (key.startsWith('internal_') && !include) {
          const index = parseInt(key.replace('internal_', ''));
          internalSkipIndices.add(index);
        }
      });
      
      // Filter out roster duplicates that user chose to skip
      const rosterSkipIndices = new Set();
      Object.entries(duplicateChoices).forEach(([key, include]) => {
        if (key.startsWith('roster_') && !include) {
          const index = parseInt(key.replace('roster_', ''));
          rosterSkipIndices.add(index);
        }
      });
      
      // Apply filters
      membersToImport = validMembers.filter((_, index) => 
        !internalSkipIndices.has(index) && !rosterSkipIndices.has(index)
      );
    }
    
    return membersToImport;
  }, [previewData, duplicateChoices]);

  // Handle proceeding to gender assignment
  const handleProceedToGenderAssignment = useCallback(() => {
    clearErrors(); // Clear any existing errors
    const membersToImport = getFilteredMembers();
    
    if (!membersToImport.length) {
      // This shouldn't happen with proper UI state, but just in case
      return;
    }

    // Initialize members with empty gender for assignment
    const membersForGenderAssignment = membersToImport.map(member => ({
      ...member,
      gender: '' // Reset gender for assignment
    }));
    
    setMembersWithGender(membersForGenderAssignment);
    setStep(4); // Gender assignment step
  }, [clearErrors, getFilteredMembers]);

  // Handle duplicate choice change
  const handleDuplicateChoiceChange = useCallback((key, include) => {
    setDuplicateChoices(prev => ({
      ...prev,
      [key]: include
    }));
  }, []);

  // Handle gender assignment for a specific member
  const handleGenderChange = useCallback((index, gender) => {
    setMembersWithGender(prev => 
      prev.map((member, i) => 
        i === index ? { ...member, gender } : member
      )
    );
  }, []);

  // Handle email update for a specific member
  const handleEmailChange = useCallback((index, email) => {
    setMemberDetails(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        email: email
      }
    }));
  }, []);



  // Handle first name update for a specific member
  const handleFirstNameChange = useCallback((index, firstName) => {
    setMembersWithGender(prev => 
      prev.map((member, i) => 
        i === index ? { ...member, firstName } : member
      )
    );
  }, []);

  // Handle last name update for a specific member
  const handleLastNameChange = useCallback((index, lastName) => {
    setMembersWithGender(prev => 
      prev.map((member, i) => 
        i === index ? { ...member, lastName } : member
      )
    );
  }, []);

  // Handle date of birth update for a specific member
  const handleDateOfBirthChange = useCallback((index, dateOfBirth) => {
    // Convert HTML date input (YYYY-MM-DD) to our backend format (YYYY-MM-DD)
    // The backend already expects YYYY-MM-DD format, so no conversion needed for storage
    setMembersWithGender(prev => 
      prev.map((member, i) => 
        i === index ? { ...member, dateOfBirth } : member
      )
    );
  }, []);

  // Convert MM-DD-YYYY to YYYY-MM-DD for date input (HTML date inputs expect YYYY-MM-DD)
  const formatDateForInput = useCallback((dateString) => {
    if (!dateString) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // If in MM-DD-YYYY format, convert to YYYY-MM-DD
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split('-');
      return `${year}-${month}-${day}`;
    }
    
    return dateString;
  }, []);

  // Handle final bulk import with all details finalized
  const handleFinalImport = useCallback(async () => {
    // Check if all members have genders assigned
    const missingGenders = membersWithGender.filter(member => !member.gender);
    if (missingGenders.length > 0) {
      setError(`Please assign gender for all ${missingGenders.length} remaining dancers`);
      return;
    }

    // Merge additional details (email only) with member data
    const finalMembersData = membersWithGender.map((member, index) => {
      const details = memberDetails[index] || {};
      
      // Format data exactly like the regular add member function
      return {
        firstName: member.firstName?.trim() || '',
        lastName: member.lastName?.trim() || '',
        email: (details.email !== undefined ? details.email : member.email)?.trim() || '',
        gender: member.gender || '',
        role: member.role || ['Dancer'], // Keep as regular array - Lambda will store as-is
        dateOfBirth: member.dateOfBirth || ''
      };
    });

    try {
      await onImport(finalMembersData);
      setStep(5); // Import complete step
    } catch (error) {
      setError(error.message || 'Failed to import members');
    }
  }, [membersWithGender, memberDetails, onImport]);

  // Render different steps
  const renderUploadStep = () => (
    <div className="space-y-6">
      {/* Template Download Section */}
      <div className={`p-4 rounded-lg border-2 border-dashed ${
        isDarkMode 
          ? 'border-primary/30 bg-primary/5' 
          : 'border-primary/30 bg-primary/5'
      }`}>
        <div className="text-center">
          <FileSpreadsheet className="mx-auto h-12 w-12 text-primary mb-3" />
          <h3 className="text-lg font-semibold mb-2">Step 1: Download Template</h3>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Download our pre-formatted Excel template for bulk importing dancers. All imported members will automatically be assigned the "Dancer" role.
          </p>
          <button
            onClick={handleDownloadTemplate}
            className="btn btn-primary btn-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </button>
        </div>
      </div>

      {/* File Upload Section */}
      <div className={`p-4 rounded-lg border-2 border-dashed ${
        isDarkMode 
          ? 'border-gray-600 bg-[#1a1f2d]' 
          : 'border-gray-300 bg-gray-50'
      }`}>
        <div className="text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold mb-2">Step 2: Upload Your File</h3>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Fill out the template and upload it here. We support CSV and Excel files.
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-outline btn-primary"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Choose File
              </>
            )}
          </button>
          
          {uploadedFile && (
            <p className="text-sm mt-2 text-success">
              Selected: {uploadedFile.name}
            </p>
          )}
        </div>
      </div>

      {/* Template Format Info */}
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-info/10' : 'bg-info/10'}`}>
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-info mt-0.5" />
          <div>
            <h4 className="font-medium text-info mb-1">Template Format - Dancers Only</h4>
            <ul className="text-sm space-y-1 text-info/80">
              <li>• <strong>First Name*</strong>, <strong>Last Name*</strong>, and <strong>Date of Birth*</strong> are required</li>
              <li>• <strong>Date of Birth*</strong> format: MM-DD-YYYY or MM/DD/YYYY (e.g., 03-15-2005 or 03/15/2005)</li>
              <li>• <strong>Email</strong> is optional but must be valid if provided</li>
              <li>• <strong>Gender</strong> will be assigned in the next step after upload</li>
              <li>• <strong>Role</strong> is automatically set to "Dancer" for all imports</li>
              <li>• This feature is designed for bulk importing dancers only</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <div className="text-center">
            <Users className="mx-auto h-8 w-8 text-primary mb-2" />
            <div className="text-2xl font-bold">{previewData?.summary.totalRows || 0}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Rows</div>
          </div>
        </div>
        
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <div className="text-center">
            <CheckCircle className="mx-auto h-8 w-8 text-success mb-2" />
            <div className="text-2xl font-bold text-success">{previewData?.summary.validRows || 0}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Valid</div>
          </div>
        </div>
        
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <div className="text-center">
            <AlertCircle className="mx-auto h-8 w-8 text-error mb-2" />
            <div className="text-2xl font-bold text-error">{previewData?.summary.invalidRows || 0}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Errors</div>
          </div>
        </div>
      </div>


      {/* Error Summary */}
      {previewData?.summary.hasErrors && (
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-error/10 border-error/30' : 'bg-error/10 border-error/30'}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-error">Rows with Errors</h4>
            <button
              onClick={() => setShowInvalidRows(!showInvalidRows)}
              className="btn btn-sm btn-ghost text-error"
            >
              {showInvalidRows ? (
                <>
                  <EyeOff className="w-4 h-4 mr-1" />
                  Hide
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Show
                </>
              )}
            </button>
          </div>
          
          {showInvalidRows && (
            <div className="space-y-2">
              {previewData.invalidMembers.map((member, index) => (
                <div key={index} className="text-sm">
                  <strong>Row {member.rowNumber}:</strong> {member.firstName} {member.lastName}
                  <ul className="ml-4 mt-1">
                    {member.errors.map((error, i) => (
                      <li key={i} className="text-error">• {error}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Valid Members Preview */}
      {previewData?.validMembers?.length > 0 && (() => {
        const { currentPageItems, PaginationControls, startIndex } = createPaginationComponent(
          previewCurrentPage,
          setPreviewCurrentPage,
          previewItemsPerPage,
          setPreviewItemsPerPage,
          previewData.validMembers.length,
          previewData.validMembers
        );

        return (
          <div>
            <h4 className="font-medium mb-3">Dancers Ready to Import ({previewData.validMembers.length})</h4>
            <div className={`border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
              <table className="table table-sm w-full">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}>
                    <th className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                      <div className="flex items-center gap-2">
                        <Hash className="h-4 w-4" />
                        #
                      </div>
                    </th>
                    <th className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>Name</th>
                    <th className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>Date of Birth</th>
                    <th className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>Email</th>
                  </tr>
                </thead>
                <tbody className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  {currentPageItems.map((member, index) => (
                    <tr key={index}>
                      <td className={`text-center font-mono text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                        {startIndex + index + 1}
                      </td>
                      <td>{member.firstName} {member.lastName}</td>
                      <td>{convertToDisplayDateFormat(member.dateOfBirth) || '-'}</td>
                      <td>{member.email || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls />
          </div>
        );
      })()}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => {
            clearErrors();
            setStep(1);
          }}
          className="btn btn-ghost"
        >
          Back
        </button>
        
        <button
          onClick={handleProceedToDuplicateResolution}
          disabled={!previewData?.validMembers?.length}
          className="btn btn-primary"
        >
          {previewData?.summary?.hasDuplicates ? 'Next: Review Duplicates' : 'Next: Assign Gender'}
        </button>
      </div>
    </div>
  );

  const renderDuplicateResolutionStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <AlertTriangle className="mx-auto h-12 w-12 text-warning mb-4" />
        <h3 className="text-xl font-semibold mb-2">Review Duplicate Dancers</h3>
        <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
          We found some duplicates. Choose which dancers to include or skip.
        </p>
      </div>

      {/* Internal Duplicates */}
      {previewData?.duplicates?.internal?.length > 0 && (
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-warning/10 border-warning/30' : 'bg-warning/10 border-warning/30'}`}>
          <h4 className="font-medium mb-3 text-warning">Duplicates Within Import File</h4>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            The same dancer appears multiple times in your file. We'll keep the first occurrence by default.
          </p>
          
          {previewData.duplicates.internal.map((duplicate, dupIndex) => (
            <div key={dupIndex} className="mb-4 last:mb-0">
              <div className="font-medium mb-2">{duplicate.message}</div>
              <div className="ml-4 space-y-2">
                {duplicate.indices.map((index, occurrenceIndex) => {
                  const member = previewData.validMembers[index];
                  const isFirst = occurrenceIndex === 0;
                  const choiceKey = `internal_${index}`;
                  const isIncluded = isFirst || duplicateChoices[choiceKey] === true;
                  
                  return (
                    <div key={index} className={`flex items-center justify-between p-3 rounded border ${
                      isFirst 
                        ? (isDarkMode ? 'bg-success/20 border-success/30' : 'bg-success/10 border-success/30')
                        : (isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300')
                    }`}>
                      <div className="flex-1">
                        <div className="font-medium">
                          {member.firstName} {member.lastName}
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Born: {convertToDisplayDateFormat(member.dateOfBirth)} • {member.email || 'No email'}
                        </div>
                        {isFirst && (
                          <div className="text-xs text-success mt-1">
                            ✓ First occurrence (will be kept)
                          </div>
                        )}
                      </div>
                      
                      {!isFirst && (
                        <div className="ml-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={isIncluded}
                              onChange={(e) => handleDuplicateChoiceChange(choiceKey, e.target.checked)}
                              className="checkbox checkbox-sm"
                            />
                            <span className="text-sm">Include anyway</span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Roster Duplicates */}
      {previewData?.duplicates?.roster?.length > 0 && (
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-info/10 border-info/30' : 'bg-info/10 border-info/30'}`}>
          <h4 className="font-medium mb-3 text-info">Already in Your Roster</h4>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            These dancers already exist in your roster. They'll be skipped by default to prevent duplicates.
          </p>
          
          <div className="space-y-3">
            {previewData.duplicates.roster.map((duplicate, index) => {
              const choiceKey = `roster_${duplicate.importIndex}`;
              const isIncluded = duplicateChoices[choiceKey] === true;
              
              return (
                <div key={index} className={`flex items-center justify-between p-3 rounded border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                }`}>
                  <div className="flex-1">
                    <div className="font-medium">
                      {duplicate.importMember.firstName} {duplicate.importMember.lastName}
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Born: {convertToDisplayDateFormat(duplicate.importMember.dateOfBirth)} • {duplicate.importMember.email || 'No email'}
                    </div>
                    <div className="text-xs text-info mt-1">
                      Already exists as: {duplicate.existingMember.firstName} {duplicate.existingMember.lastName}
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isIncluded}
                        onChange={(e) => handleDuplicateChoiceChange(choiceKey, e.target.checked)}
                        className="checkbox checkbox-sm"
                      />
                      <span className="text-sm">Import anyway</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Import Summary */}
      <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-700/30' : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h5 className="font-medium">Import Summary</h5>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {(() => {
                const filteredCount = getFilteredMembers().length;
                if (filteredCount === 0) {
                  return "No dancers selected for import. Please include at least one dancer.";
                } else if (filteredCount === 1) {
                  return "1 dancer will be imported.";
                } else {
                  return `${filteredCount} dancers will be imported.`;
                }
              })()}
            </p>
          </div>
          <div className={`text-2xl font-bold ${
            getFilteredMembers().length === 0 
              ? 'text-warning' 
              : 'text-success'
          }`}>
            {getFilteredMembers().length}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4">
        <button
          onClick={() => {
            clearErrors();
            setStep(2);
          }}
          className="btn btn-ghost"
        >
          Back
        </button>
        
        <button
          onClick={handleProceedToGenderAssignment}
          disabled={getFilteredMembers().length === 0}
          className="btn btn-primary"
        >
          Next: Finalize Details
        </button>
      </div>
    </div>
  );

  const renderGenderAssignmentStep = () => {
    const { currentPageItems, PaginationControls, startIndex } = createPaginationComponent(
      genderCurrentPage,
      setGenderCurrentPage,
      genderItemsPerPage,
      setGenderItemsPerPage,
      membersWithGender.length,
      membersWithGender
    );

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <Users className="mx-auto h-12 w-12 text-primary mb-3" />
          <h3 className="text-lg font-semibold mb-2">Finalize Dancer Details</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Assign gender (required) and optionally update emails.
          </p>
          <div className="mt-3">
            <span className="text-sm">
              {membersWithGender.filter(m => m.gender).length} of {membersWithGender.length} dancers have gender assigned
            </span>
          </div>
        </div>

        <div className="space-y-2">
          {currentPageItems.map((member, pageIndex) => {
            const actualIndex = startIndex + pageIndex; // Calculate the actual index in the full array
            const details = memberDetails[actualIndex] || {};
            const currentEmail = details.email !== undefined ? details.email : member.email;


            return (
              <div key={actualIndex} className={`flex items-center gap-4 p-3 rounded-lg border ${
                isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-gray-50'
              }`}>
                {/* Number */}
                <div className={`flex-shrink-0 w-12 text-center font-mono text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {actualIndex + 1}
                </div>


              {/* First Name */}
              <div className="flex-shrink-0 w-24">
                <input
                  type="text"
                  value={member.firstName || ''}
                  onChange={(e) => handleFirstNameChange(actualIndex, e.target.value)}
                  placeholder="First"
                  className={`input input-xs w-full ${isDarkMode ? 'bg-gray-700 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              {/* Last Name */}
              <div className="flex-shrink-0 w-24">
                <input
                  type="text"
                  value={member.lastName || ''}
                  onChange={(e) => handleLastNameChange(actualIndex, e.target.value)}
                  placeholder="Last"
                  className={`input input-xs w-full ${isDarkMode ? 'bg-gray-700 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              {/* Date of Birth with Calendar */}
              <div className="flex-shrink-0 w-32">
                <input
                  type="date"
                  value={formatDateForInput(member.dateOfBirth) || ''}
                  onChange={(e) => handleDateOfBirthChange(actualIndex, e.target.value)}
                  className={`input input-xs w-full ${isDarkMode ? 'bg-gray-700 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              {/* Gender Selection - Required */}
              <div className="flex-shrink-0 w-28">
                <select
                  value={member.gender || ''}
                  onChange={(e) => handleGenderChange(actualIndex, e.target.value)}
                  className={`select select-xs w-full ${
                    !member.gender ? 'select-error' : 'select-success'
                  } ${isDarkMode ? 'bg-gray-700 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">Gender *</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                </select>
              </div>

              {/* Email Input - Optional */}
              <div className="flex-1 min-w-0 max-w-48">
                <input
                  type="email"
                  value={currentEmail || ''}
                  onChange={(e) => handleEmailChange(actualIndex, e.target.value)}
                  placeholder="Email (optional)"
                  className={`input input-xs w-full ${isDarkMode ? 'bg-gray-700 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            </div>
          );
          })}
        </div>

        <PaginationControls />

        {/* Progress Bar */}
        {isSubmitting && importProgress.total > 0 && (
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex justify-between text-sm mb-2">
              <span>{importProgress.message}</span>
              <span>{importProgress.current} of {importProgress.total}</span>
            </div>
            <div className={`w-full bg-gray-200 rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : ''}`}>
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => {
            clearErrors();
            setStep(previewData?.summary?.hasDuplicates ? 3 : 2);
          }}
          className="btn btn-ghost"
        >
          Back
        </button>
        
        <button
          onClick={handleFinalImport}
          disabled={membersWithGender.filter(m => m.gender).length !== membersWithGender.length || isSubmitting}
          className="btn btn-primary"
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>
                {importProgress.message || 'Importing...'}
                {importProgress.total > 0 && (
                  <span className="ml-2">
                    ({importProgress.current}/{importProgress.total})
                  </span>
                )}
              </span>
            </div>
          ) : (
            `Import ${membersWithGender.length} Dancers`
          )}
        </button>
        </div>
      </div>
    );
  };

  const renderSuccessStep = () => (
    <div className="text-center space-y-4">
      <CheckCircle className="mx-auto h-16 w-16 text-success" />
      <h3 className="text-2xl font-semibold text-success">Import Successful!</h3>
      <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Successfully imported {previewData?.validMembers?.length || 0} dancers to your roster.
      </p>
      <button
        onClick={onClose}
        className="btn btn-primary"
      >
        Done
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
        isDarkMode ? 'bg-[#1a1f2d]' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDarkMode ? 'border-gray-600' : 'border-gray-200'
        }`}>
          <h2 className="text-2xl font-bold">
            {step === 1 && 'Bulk Import Dancers'}
            {step === 2 && 'Preview Dancer Import'}
            {step === 3 && 'Review Duplicates'}
            {step === 4 && 'Finalize Details'}
            {step === 5 && 'Import Complete'}
          </h2>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-circle"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="alert alert-error mb-4">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <button
                onClick={clearErrors}
                className="btn btn-ghost btn-sm ml-auto"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 1 && renderUploadStep()}
          {step === 2 && renderPreviewStep()}
          {step === 3 && renderDuplicateResolutionStep()}
          {step === 4 && renderGenderAssignmentStep()}
          {step === 5 && renderSuccessStep()}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
